import { ChevronDown } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import { cn } from './cn';
import { fieldLabel, focusAfter, listPosition, nextMatch } from './select-logic';
import { SelectList } from './SelectList';
import { useDismiss } from './use-dismiss';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  /** id del disparador: lo usa un <label htmlFor> o el envoltorio <label className="fld">. */
  id?: string;
  /** Nombre accesible cuando no hay etiqueta visible. */
  ariaLabel?: string;
  /** Nombre de la lista; por defecto, el del disparador. */
  listLabel?: string;
  ariaDescribedBy?: string;
  invalid?: boolean;
  /** Texto cuando el valor no corresponde a ninguna opción. */
  placeholder?: string;
  disabled?: boolean;
  /** Contenido antes del valor (p. ej. el color de la empresa). */
  leading?: ReactNode;
  className?: string;
}

/**
 * Select del QMS (UX-COMP-01): reemplaza al <select> nativo, cuya lista dibuja el
 * navegador sin el estilo del sistema. Patrón "select-only combobox" de WAI-ARIA: el
 * disparador (role combobox) abre la lista con clic, Enter, Espacio o las flechas; en la
 * lista, flechas (saltan las deshabilitadas), Home, End, letras para buscar, Enter o
 * Espacio para elegir; Escape cierra solo la lista (también dentro de un diálogo), Tab
 * cierra y sigue desde el disparador, y un clic afuera cierra sin cambiar. La lista se
 * dibuja en el <body> con posición fija: ningún contenedor con scroll la recorta.
 */
export function Select({
  value,
  options,
  onChange,
  id,
  ariaLabel,
  listLabel,
  ariaDescribedBy,
  invalid = false,
  placeholder = 'Selecciona…',
  disabled = false,
  leading,
  className,
}: SelectProps): ReactElement {
  const autoId = useId();
  const triggerId = id ?? `${autoId}-trigger`;
  const listId = `${autoId}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState<CSSProperties>({});
  // Nombre tomado de la etiqueta del campo (sin el valor que muestra el disparador).
  const [labelText, setLabelText] = useState<string | undefined>(undefined);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const typed = useRef({ text: '', at: 0 });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const enabled = options.flatMap((o, i) => (o.disabled === true ? [] : [i]));
  const name = ariaLabel ?? labelText;

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, [wrapRef, listRef], triggerRef);

  useEffect(() => {
    setLabelText(fieldLabel(triggerRef.current));
  }, []);

  // Opciones nuevas con la lista abierta (p. ej. un refetch): el índice activo vuelve a
  // una opción que existe y se puede elegir.
  const activeUsable = enabled.includes(active);
  const fallback = enabled.includes(selectedIndex) ? selectedIndex : (enabled[0] ?? -1);
  useEffect(() => {
    if (open && !activeUsable) {
      setActive(fallback);
    }
  }, [open, activeUsable, fallback]);

  useEffect(() => {
    if (open && active >= 0) {
      optionRefs.current[active]?.focus();
    }
  }, [open, active, options]);

  // La lista es fija respecto de la ventana: si la página se desplaza o cambia de tamaño
  // se cierra, en vez de quedar flotando lejos del campo.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onMove = (e: Event): void => {
      if (!listRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  function openList(): void {
    const trigger = triggerRef.current;
    if (disabled || options.length === 0 || trigger === null) {
      return;
    }
    setPosition(listPosition(trigger.getBoundingClientRect(), window));
    setActive(fallback);
    setOpen(true);
  }

  function closeToTrigger(): void {
    triggerRef.current?.focus();
    setOpen(false);
  }

  function choose(index: number): void {
    const option = options[index];
    if (option === undefined || option.disabled === true) {
      return;
    }
    closeToTrigger();
    if (option.value !== value) {
      onChange(option.value);
    }
  }

  function move(delta: 1 | -1): void {
    const at = enabled.indexOf(active);
    const next = enabled[Math.min(enabled.length - 1, Math.max(0, at + delta))];
    if (next !== undefined) {
      setActive(next);
    }
  }

  function onListKey(e: KeyboardEvent<HTMLUListElement>): void {
    if (e.key === 'Escape') {
      // Solo la lista: el diálogo que la contiene no debe cerrarse con ella.
      e.preventDefault();
      e.stopPropagation();
      closeToTrigger();
      return;
    }
    if (e.key === 'Tab') {
      // Como tabular desde el disparador: Shift+Tab vuelve a él, Tab sigue al próximo.
      e.preventDefault();
      closeToTrigger();
      if (!e.shiftKey && triggerRef.current !== null) {
        focusAfter(triggerRef.current);
      }
      return;
    }
    const keys: Record<string, () => void> = {
      ArrowDown: () => move(1),
      ArrowUp: () => move(-1),
      Home: () => setActive(enabled[0] ?? -1),
      End: () => setActive(enabled.at(-1) ?? -1),
      Enter: () => choose(active),
      ' ': () => choose(active),
    };
    const action = keys[e.key];
    if (action !== undefined) {
      e.preventDefault();
      action();
      return;
    }
    if (e.key.length === 1) {
      const now = Date.now();
      const buffer = now - typed.current.at < 600 ? typed.current.text + e.key : e.key;
      typed.current = { text: buffer, at: now };
      const hit = nextMatch(options, enabled, active, buffer);
      if (hit !== undefined) {
        setActive(hit);
      }
    }
  }

  return (
    <div className={cn('sel', className)} ref={wrapRef}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        role="combobox"
        className={cn('sel-trigger', invalid && 'is-invalid')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={name}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            openList();
          }
        }}
      >
        {leading}
        <span className={cn('sel-value', selected === undefined && 'is-placeholder')}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <SelectList
          id={listId}
          listRef={listRef}
          optionRefs={optionRefs}
          options={options}
          value={value}
          label={listLabel ?? name}
          position={position}
          onKeyDown={onListKey}
          onChoose={choose}
        />
      )}
    </div>
  );
}
